import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';

export type FeatureFlagTargeting = 'all_tenants' | 'by_plan' | 'by_tenant';

export interface FeatureFlagListPayload {
  page?: number;
  limit?: number;
  productId?: number;
  targeting?: FeatureFlagTargeting;
  statusId?: number;
}

export interface FeatureFlagFormPayload {
  flag_code: string;
  flag_name: string;
  product_id: number;
  default_state: boolean;
  targeting: FeatureFlagTargeting;
  status_id: number;
  description?: string;
  notes?: string;
}

const list = (payload: FeatureFlagListPayload) => {
  const { page, limit, productId, targeting, statusId } = payload;
  const params: Record<string, string> = {};
  if (page) params.page = String(page);
  if (limit) params.limit = String(limit);
  if (productId) params.productId = String(productId);
  if (targeting) params.targeting = targeting;
  if (statusId) params.statusId = String(statusId);
  const query = new URLSearchParams(params).toString();
  return http.get(`${API_ENDPOINTS.featureFlags}${query ? `?${query}` : ''}`);
};

const get = (id: number | string) => http.get(`${API_ENDPOINTS.featureFlags}/${id}`);

const checkCode = (flagCode: string, productId: number, excludeId?: number) => {
  const params: Record<string, string> = { flagCode, productId: String(productId) };
  if (excludeId) params.excludeId = String(excludeId);
  return http.get(`${API_ENDPOINTS.featureFlags}/check-code?${new URLSearchParams(params).toString()}`);
};

const create = (payload: FeatureFlagFormPayload) => http.post(API_ENDPOINTS.featureFlags, payload);

const update = (id: number | string, payload: Partial<FeatureFlagFormPayload>) =>
  http.patch(`${API_ENDPOINTS.featureFlags}/${id}`, payload);

const checkDelete = (id: number | string) => http.get(`${API_ENDPOINTS.featureFlags}/${id}/delete-check`);

const remove = (id: number | string, reason: string) =>
  http.delete(`${API_ENDPOINTS.featureFlags}/${id}`, { data: { reason } });

const listOverrides = (id: number | string) => http.get(`${API_ENDPOINTS.featureFlags}/${id}/overrides`);

const setOverride = (id: number | string, payload: { tenantId: number; overrideState: boolean; reason: string }) =>
  http.post(`${API_ENDPOINTS.featureFlags}/${id}/overrides`, payload);

const removeOverride = (id: number | string, tenantId: number | string) =>
  http.delete(`${API_ENDPOINTS.featureFlags}/${id}/overrides/${tenantId}`);

const getEffectiveValue = (id: number | string, tenantId: number | string) =>
  http.get(`${API_ENDPOINTS.featureFlags}/${id}/effective-value/${tenantId}`);

const kill = (id: number | string, reason: string) => http.post(`${API_ENDPOINTS.featureFlags}/${id}/kill`, { reason });

const liftKill = (id: number | string, reason: string) =>
  http.post(`${API_ENDPOINTS.featureFlags}/${id}/kill/lift`, { reason });

const getMatrix = (productId: number) => http.get(`${API_ENDPOINTS.featureFlags}/matrix?productId=${productId}`);

const FeatureFlagService = {
  list,
  get,
  checkCode,
  create,
  update,
  checkDelete,
  remove,
  listOverrides,
  setOverride,
  removeOverride,
  getEffectiveValue,
  kill,
  liftKill,
  getMatrix,
};

export default FeatureFlagService;
